import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, MapPin, Package, XCircle, Store, Clock } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { displayName } from "@/i18n";

// Phase G (Feb 2026): Route History — polyline + colored stop pins + chronological side panel.
// Filters: person + date. Roles allowed: management, super_admin, manager (district-scoped on backend).
const orderIcon = new L.DivIcon({
  className: "",
  html: `<div style="background:#34C759;width:26px;height:26px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);color:white;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:12px;">O</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});
const noSaleIcon = new L.DivIcon({
  className: "",
  html: `<div style="background:#FF3B30;width:26px;height:26px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);color:white;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:12px;">N</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});
const shopIcon = new L.DivIcon({
  className: "",
  html: `<div style="background:#0055FF;width:22px;height:22px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

export default function RouteHistory() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [people, setPeople] = useState([]);
  const [userId, setUserId] = useState(params.get("user_id") || "");
  const [day, setDay] = useState(params.get("date") || new Date().toISOString().slice(0, 10));
  const [pings, setPings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [noSales, setNoSales] = useState([]);
  const [personInfo, setPersonInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load list of trackable users (salesmen + managers)
  useEffect(() => {
    Promise.all([
      api.get("/users", { params: { role: "salesman" } }).then(r => r.data || []).catch(() => []),
      api.get("/users", { params: { role: "manager" } }).then(r => r.data || []).catch(() => []),
    ]).then(([sm, mg]) => {
      const list = [...sm, ...mg].filter(u => u.active !== false);
      list.sort((a, b) => (a.role || "").localeCompare(b.role) || (a.name || "").localeCompare(b.name));
      setPeople(list);
      if (!userId && list.length) setUserId(list[0].id);
    });
  }, []);

  // Fetch route data
  useEffect(() => {
    if (!userId || !day) return;
    setLoading(true);
    Promise.all([
      api.get("/tracking/history", { params: { user_id: userId, date_str: day } }).then(r => r.data),
      api.get("/orders", { params: { salesman_id: userId, start_date: day, end_date: day } }).then(r => r.data || []).catch(() => []),
      api.get("/no-sales", { params: { salesman_id: userId, date_from: day, date_to: day } }).then(r => r.data || []).catch(() => []),
    ]).then(([hist, ords, ns]) => {
      setPings(hist.pings || []);
      setPersonInfo(hist.user || null);
      setOrders(ords);
      setNoSales(ns);
    }).finally(() => setLoading(false));
    setParams({ user_id: userId, date: day }, { replace: true });
  }, [userId, day]);

  // Build polyline coords
  const path = useMemo(() => pings.filter(p => p.lat && p.lng).map(p => [p.lat, p.lng]), [pings]);

  // Pins
  const orderPins = useMemo(() => orders
    .filter(o => o.delivery_geo && o.delivery_geo.lat && o.delivery_geo.lng)
    .map(o => ({
      id: `o-${o.id}`, kind: "order",
      lat: o.delivery_geo.lat, lng: o.delivery_geo.lng,
      time: o.delivered_at || o.created_at,
      title: o.shop_name || o.shop_name_en || "—",
      sub: `${o.total_units || 0} ${t("boxes")} • ${o.status || ""}`,
    })), [orders, t]);

  const noSalePins = useMemo(() => noSales
    .filter(n => n.geo && n.geo.lat && n.geo.lng)
    .map(n => ({
      id: `n-${n.id}`, kind: "nosale",
      lat: n.geo.lat, lng: n.geo.lng,
      time: n.created_at,
      title: n.shop_name || (lang === "ta" ? "வாக்-இன்" : "Walk-in"),
      sub: t(n.reason_code || "reason_other") + (n.custom_reason ? ` • ${n.custom_reason}` : ""),
    })), [noSales, lang, t]);

  const stops = useMemo(() => {
    const all = [...orderPins, ...noSalePins];
    all.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return all;
  }, [orderPins, noSalePins]);

  const center = useMemo(() => {
    if (path.length) return path[0];
    if (stops.length) return [stops[0].lat, stops[0].lng];
    return [13.0827, 80.2707]; // Chennai default
  }, [path, stops]);

  const personLabel = (p) => {
    const name = lang === "ta" && p.name_ta ? p.name_ta : p.name;
    const role = p.role === "manager" ? t("role") || "Manager" : (lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman");
    return `${name} (${role})`;
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-10">
      <TopBar
        title={lang === "ta" ? "வழி வரலாறு" : "Route History"}
        onBack={() => nav("/management/live-tracking")}
      />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4 fade-up">
        {/* Filter bar */}
        <div className="bg-white rounded-2xl soft-shadow p-4 flex flex-wrap gap-3 items-end" data-testid="rh-filters">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold block mb-1">
              {lang === "ta" ? "நபர்" : "Person"}
            </label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="rh-person-select"
              className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 font-semibold text-[#1D1D1F]"
            >
              <option value="">{lang === "ta" ? "ஒருவரை தேர்ந்தெடுங்கள்" : "Select a person…"}</option>
              {people.map(p => (
                <option key={p.id} value={p.id}>{personLabel(p)}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[180px]">
            <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold block mb-1">
              {t("date_range") || "Date"}
            </label>
            <input
              type="date" value={day}
              onChange={(e) => setDay(e.target.value)}
              data-testid="rh-date-input"
              className="bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 font-semibold text-[#1D1D1F]"
            />
          </div>
          <button
            onClick={() => nav("/management/live-tracking")}
            data-testid="rh-back-btn"
            className="px-4 py-2.5 rounded-xl bg-gray-100 text-[#1D1D1F] hover:bg-gray-200 font-bold text-sm flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> {t("back")}
          </button>
        </div>

        {/* Summary chips */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Chip color="bg-[#5856D6]" label={lang === "ta" ? "GPS பாயிண்ட்கள்" : "GPS Points"} value={pings.length} />
          <Chip color="bg-[#34C759]" label={t("orders")} value={orderPins.length} />
          <Chip color="bg-[#FF3B30]" label={lang === "ta" ? "விற்பனை இல்லை" : "No-Sale"} value={noSalePins.length} />
          <Chip color="bg-[#0055FF]" label={lang === "ta" ? "மொத்த நிறுத்தங்கள்" : "Stops"} value={stops.length} />
        </div>

        {/* Map + Side panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Map */}
          <div className="lg:col-span-2 bg-white rounded-2xl soft-shadow overflow-hidden" style={{ height: 540 }} data-testid="rh-map">
            {loading ? (
              <div className="h-full grid place-items-center text-[#86868B]">{t("loading")}</div>
            ) : (path.length === 0 && stops.length === 0) ? (
              <div className="h-full grid place-items-center text-[#86868B] p-6 text-center" data-testid="rh-empty">
                <div>
                  <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <div className="font-bold">{lang === "ta" ? "தரவு இல்ல" : "No data for selected day"}</div>
                  <div className="text-xs mt-1">
                    {lang === "ta" ? "GPS பாயிண்ட்கள் இல்ல" : "No GPS pings or visits found."}
                  </div>
                </div>
              </div>
            ) : (
              <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {path.length >= 2 && (
                  <Polyline positions={path} pathOptions={{ color: "#5856D6", weight: 4, opacity: 0.85 }} />
                )}
                {/* Direction breadcrumbs */}
                {pings.filter((_, i) => i % 5 === 0).map((p, idx) => p.lat && (
                  <CircleMarker key={`p-${idx}`} center={[p.lat, p.lng]}
                    pathOptions={{ color: "#5856D6", fillColor: "#5856D6", fillOpacity: 0.6 }} radius={3} />
                ))}
                {orderPins.map(s => (
                  <Marker key={s.id} position={[s.lat, s.lng]} icon={orderIcon}>
                    <Popup>
                      <div className="font-bold">{s.title}</div>
                      <div className="text-xs">{s.sub}</div>
                      <div className="text-xs text-gray-500 mt-1">{fmtTime(s.time)}</div>
                    </Popup>
                  </Marker>
                ))}
                {noSalePins.map(s => (
                  <Marker key={s.id} position={[s.lat, s.lng]} icon={noSaleIcon}>
                    <Popup>
                      <div className="font-bold">{s.title}</div>
                      <div className="text-xs text-red-600">{s.sub}</div>
                      <div className="text-xs text-gray-500 mt-1">{fmtTime(s.time)}</div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </div>

          {/* Side panel — chronological stops */}
          <div className="bg-white rounded-2xl soft-shadow overflow-hidden flex flex-col" style={{ height: 540 }}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="heading-font font-bold text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#5856D6]" />
                {lang === "ta" ? "காலவரிசை" : "Chronological Stops"} ({stops.length})
              </h3>
              <div className="text-xs text-[#86868B] mt-1 flex flex-wrap gap-3">
                <Legend dot="#34C759" label={lang === "ta" ? "ஆர்டர்" : "Order"} />
                <Legend dot="#FF3B30" label={lang === "ta" ? "விற்பனை இல்ல" : "No-sale"} />
                <Legend dot="#5856D6" label={lang === "ta" ? "வழி" : "Path"} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="rh-stops-panel">
              {loading ? (
                <div className="p-6 text-center text-[#86868B]">{t("loading")}</div>
              ) : stops.length === 0 ? (
                <div className="p-6 text-center text-[#86868B] text-sm">
                  {lang === "ta" ? "எந்த நிறுத்தமும் இல்ல" : "No stops on this day"}
                </div>
              ) : (
                <ol className="divide-y divide-gray-100">
                  {stops.map((s, idx) => (
                    <li key={s.id} className="p-3 flex gap-3 items-start" data-testid={`rh-stop-${idx}`}>
                      <div className={`w-7 h-7 rounded-full grid place-items-center text-white text-xs font-bold shrink-0 ${s.kind === "order" ? "bg-[#34C759]" : "bg-[#FF3B30]"}`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-[#1D1D1F] truncate flex items-center gap-1.5">
                          {s.kind === "order" ? <Package className="w-3.5 h-3.5 text-[#34C759]" /> : <XCircle className="w-3.5 h-3.5 text-[#FF3B30]" />}
                          {s.title}
                        </div>
                        <div className="text-xs text-[#86868B] truncate mt-0.5">{s.sub}</div>
                        <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {fmtTime(s.time)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>

        {personInfo && (
          <div className="text-xs text-[#86868B] text-center" data-testid="rh-person-info">
            {lang === "ta" && personInfo.name_ta ? personInfo.name_ta : personInfo.name} • {personInfo.role}
          </div>
        )}
      </div>
    </div>
  );
}

const Chip = ({ color, label, value }) => (
  <div className="bg-white rounded-2xl p-3 soft-shadow flex items-center gap-3">
    <div className={`w-9 h-9 rounded-xl ${color} grid place-items-center`}>
      <Store className="w-4 h-4 text-white" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{label}</div>
      <div className="heading-font text-xl font-bold">{value}</div>
    </div>
  </div>
);

const Legend = ({ dot, label }) => (
  <span className="flex items-center gap-1">
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dot }} />
    {label}
  </span>
);
